/**
 * Timezone-safe helpers for <input type="datetime-local"> ↔ backend/PostPeer.
 *
 * Root cause fix (iter92): <input type="datetime-local"> emits a NAIVE
 * wall-clock string in the user's LOCAL browser timezone (e.g. "2026-07-16T05:56"
 * for a CEST user). Sending that raw string to the backend caused the
 * normalizer to stamp it as UTC — publishing the post 2 hours off wall-clock.
 *
 * Contract used everywhere:
 *   • Wire (backend/PostPeer) always uses tz-aware ISO ("...Z" or "…+00:00").
 *   • UI (<input type=datetime-local>) always uses NAIVE local wall-clock.
 *
 * Convert ONLY at the boundary of those two worlds.
 */

/**
 * Convert a naive local wall-clock string (from a datetime-local input) to
 * a UTC-stamped ISO string suitable for the backend / PostPeer.
 *
 *   "2026-07-16T05:56"      (CEST browser) → "2026-07-16T03:56:00.000Z"
 *   "2026-07-16T05:56:00"                  → "2026-07-16T03:56:00.000Z"
 *   "2026-07-16T03:56:00Z"  (already UTC)  → "2026-07-16T03:56:00.000Z"
 *   ""                                     → ""
 */
export function localInputToUTC(value) {
  if (!value) return '';
  // Fully-qualified ISO already? Trust it.
  if (/[Zz]|[+-]\d{2}:?\d{2}$/.test(value)) {
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : d.toISOString();
  }
  // Naive → JS constructor interprets it as LOCAL time. Exactly what we want.
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toISOString();
}

/**
 * Convert an ISO-with-offset (or naive UTC — for legacy DB values) to a naive
 * local wall-clock string for <input type="datetime-local"> prefill.
 *
 *   "2026-07-16T03:56:00Z"        (CEST browser) → "2026-07-16T05:56"
 *   "2026-07-16T05:56:00+02:00"                   → "2026-07-16T05:56"
 *   "2026-07-16T05:56:00"          (naive legacy) → "2026-07-16T05:56"  (best-effort)
 *   ""                                             → ""
 */
export function utcToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
